import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Plus, PencilSimple, Trash, Truck, Paperclip, Link as LinkIcon, CheckCircle } from "@phosphor-icons/react";
import { Modal, Field } from "./Suppliers";
import { toast } from "sonner";

const STATUSES = [
  { value: "scheduled", label: "Scheduled", color: "brut-tag-yellow" },
  { value: "picked", label: "Picked", color: "brut-tag-blue" },
  { value: "in_transit", label: "In Transit", color: "brut-tag-dark" },
  { value: "delivered", label: "Delivered", color: "brut-tag-green" },
  { value: "cancelled", label: "Cancelled", color: "brut-tag-red" },
];
const badge = (s) => STATUSES.find((x) => x.value === s) || STATUSES[0];

export default function Dispatches() {
  const { user } = useAuth();
  const canEdit = user?.role === "admin" || user?.role === "operator";
  const canDelete = user?.role === "admin";
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");

  const load = async () => setItems((await api.get("/dispatches")).data);
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this dispatch record?")) return;
    try { await api.delete(`/dispatches/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const filtered = statusFilter === "all" ? items : items.filter((x) => x.status === statusFilter);

  return (
    <div className="space-y-6" data-testid="dispatches-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-4xl uppercase tracking-tighter">Dispatch Tracker</h1>
          <p className="text-xs text-zinc-600 mt-1">Shipments to customers — pickup schedule, BOL & packing list.</p>
        </div>
        {canEdit && (
          <button onClick={() => { setEditing(null); setOpen(true); }} className="brut-btn brut-btn-primary px-4 py-2 inline-flex items-center gap-2" data-testid="new-dispatch-btn">
            <Plus size={14} weight="bold" /> New Dispatch
          </button>
        )}
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <button onClick={() => setStatusFilter("all")} className={`brut-tag ${statusFilter === "all" ? "brut-tag-dark" : "brut-tag-white"}`} data-testid="filter-all">All ({items.length})</button>
        {STATUSES.map((s) => {
          const n = items.filter((x) => x.status === s.value).length;
          return (
            <button key={s.value} onClick={() => setStatusFilter(s.value)} className={`brut-tag ${statusFilter === s.value ? "brut-tag-dark" : s.color}`} data-testid={`filter-${s.value}`}>
              {s.label} ({n})
            </button>
          );
        })}
      </div>

      <div className="brut-card overflow-x-auto">
        <table className="brut-table" data-testid="dispatches-table">
          <thead><tr>
            <th>Invoice #</th><th>PO #</th><th>Project</th>
            <th className="text-right">Qty</th><th className="text-right">Total</th>
            <th>Carrier</th>
            <th>Pickup Req.</th><th>Actual Pickup</th>
            <th>Status</th><th>Docs</th><th></th>
          </tr></thead>
          <tbody>
            {filtered.length === 0 && <tr><td colSpan={11} className="text-center py-8 text-zinc-500">No dispatches yet.</td></tr>}
            {filtered.map((d) => {
              const b = badge(d.status);
              return (
                <tr key={d.id} data-testid={`dispatch-row-${d.invoice_number}`}>
                  <td className="font-bold inline-flex items-center gap-2"><Truck size={14} weight="bold" /> {d.invoice_number}</td>
                  <td className="font-mono-ui text-xs">{d.po_number || "—"}</td>
                  <td className="text-xs">{d.project || "—"}</td>
                  <td className="text-right font-mono-ui">{d.qty ?? "—"}</td>
                  <td className="text-right font-mono-ui">{d.total ? `$${d.total}` : "—"}</td>
                  <td className="text-xs">{d.carrier || "—"}</td>
                  <td className="font-mono-ui text-xs">{d.pickup_requested_at || "—"}</td>
                  <td className="font-mono-ui text-xs">{d.actual_pickup_at ? <span className="brut-tag brut-tag-green">{d.actual_pickup_at}</span> : "—"}</td>
                  <td><span className={`brut-tag ${b.color}`}>{b.label}</span></td>
                  <td className="whitespace-nowrap">
                    {d.bol_url && <a href={d.bol_url} target="_blank" rel="noopener noreferrer" className="brut-tag brut-tag-blue text-[10px] mr-1" title="Signed BOL" data-testid={`bol-${d.invoice_number}`}><Paperclip size={9} weight="bold" /> BOL</a>}
                    {d.packing_list_url && <a href={d.packing_list_url} target="_blank" rel="noopener noreferrer" className="brut-tag brut-tag-blue text-[10px]" title="Packing List" data-testid={`pl-${d.invoice_number}`}><Paperclip size={9} weight="bold" /> PL</a>}
                    {!d.bol_url && !d.packing_list_url && <span className="text-xs text-zinc-400">—</span>}
                  </td>
                  <td className="text-right whitespace-nowrap">
                    {canEdit && d.status === "scheduled" && (
                      <button
                        onClick={async () => {
                          const now = new Date().toISOString().slice(0, 10);
                          try {
                            await api.put(`/dispatches/${d.id}`, { ...d, status: "picked", actual_pickup_at: d.actual_pickup_at || now });
                            toast.success("Marked as Picked"); load();
                          } catch (e) { toast.error(formatApiError(e)); }
                        }}
                        className="brut-tag brut-tag-green mr-1"
                        title="Mark as picked (stamps actual pickup date)"
                        data-testid={`pick-${d.invoice_number}`}
                      >
                        <CheckCircle size={10} weight="bold" /> Pick
                      </button>
                    )}
                    {canEdit && <button onClick={() => { setEditing(d); setOpen(true); }} className="brut-tag brut-tag-white mr-1" data-testid={`edit-${d.invoice_number}`}><PencilSimple size={10} weight="bold" /></button>}
                    {canDelete && <button onClick={() => remove(d.id)} className="brut-tag brut-tag-red" data-testid={`delete-${d.invoice_number}`}><Trash size={10} weight="bold" /></button>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {open && <DispatchModal initial={editing} onClose={() => { setOpen(false); setEditing(null); }} onSaved={() => { setOpen(false); setEditing(null); load(); }} />}
    </div>
  );
}

function DispatchModal({ initial, onClose, onSaved }) {
  const [f, setF] = useState({
    invoice_number: initial?.invoice_number || "",
    po_number: initial?.po_number || "",
    project: initial?.project || "",
    qty: initial?.qty ?? "",
    price: initial?.price ?? "",
    total: initial?.total ?? "",
    carrier: initial?.carrier || "",
    pickup_requested_at: initial?.pickup_requested_at || "",
    actual_pickup_at: initial?.actual_pickup_at || "",
    ship_date: initial?.ship_date || "",
    status: initial?.status || "scheduled",
    bol_url: initial?.bol_url || "",
    packing_list_url: initial?.packing_list_url || "",
    notes: initial?.notes || "",
  });
  const [saving, setSaving] = useState(false);

  const upd = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    if (!f.invoice_number.trim()) return toast.error("Invoice # is required");
    setSaving(true);
    try {
      const payload = {
        ...f,
        qty: f.qty === "" ? 0 : parseFloat(f.qty),
        price: f.price === "" ? 0 : parseFloat(f.price),
        total: f.total === "" ? 0 : parseFloat(f.total),
        pickup_requested_at: f.pickup_requested_at || null,
        actual_pickup_at: f.actual_pickup_at || null,
        ship_date: f.ship_date || null,
      };
      if (initial?.id) await api.put(`/dispatches/${initial.id}`, payload);
      else await api.post("/dispatches", payload);
      toast.success(initial ? "Updated" : "Created");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Modal title={initial ? "Edit Dispatch" : "New Dispatch"} onClose={onClose} wide>
      <form onSubmit={save} className="space-y-3">
        <div className="grid grid-cols-3 gap-3">
          <Field label="Invoice # *" required value={f.invoice_number} onChange={(v) => upd("invoice_number", v)} testid="d-invoice" />
          <Field label="PO #" value={f.po_number} onChange={(v) => upd("po_number", v)} testid="d-po" />
          <Field label="Project" value={f.project} onChange={(v) => upd("project", v)} testid="d-project" />
        </div>
        <div className="grid grid-cols-3 gap-3">
          <Field label="Qty" value={f.qty} onChange={(v) => upd("qty", v)} testid="d-qty" />
          <Field label="Unit Price" value={f.price} onChange={(v) => upd("price", v)} testid="d-price" />
          <Field label="Total" value={f.total} onChange={(v) => upd("total", v)} testid="d-total" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Carrier" value={f.carrier} onChange={(v) => upd("carrier", v)} testid="d-carrier" placeholder="e.g. FedEx, XPO" />
          <div>
            <label className="brut-label">Status</label>
            <select value={f.status} onChange={(e) => upd("status", e.target.value)} className="brut-input mt-1" data-testid="d-status">
              {STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div><label className="brut-label">Pickup Requested</label><input type="date" value={f.pickup_requested_at} onChange={(e) => upd("pickup_requested_at", e.target.value)} className="brut-input mt-1" data-testid="d-pickup-req" /></div>
          <div><label className="brut-label">Actual Pickup</label><input type="date" value={f.actual_pickup_at} onChange={(e) => upd("actual_pickup_at", e.target.value)} className="brut-input mt-1" data-testid="d-pickup-actual" /></div>
          <div><label className="brut-label">Ship Date</label><input type="date" value={f.ship_date} onChange={(e) => upd("ship_date", e.target.value)} className="brut-input mt-1" data-testid="d-ship-date" /></div>
        </div>
        <div className="brut-border p-3 space-y-2 bg-blue-50">
          <div className="text-xs font-mono-ui text-blue-800 uppercase tracking-widest flex items-center gap-2"><LinkIcon size={12} weight="bold" /> Document Links</div>
          <p className="text-[10px] text-zinc-600">Paste a shared-drive URL (Google Drive, Dropbox, SharePoint) to the scanned PDF.</p>
          <Field label="Signed BOL URL" value={f.bol_url} onChange={(v) => upd("bol_url", v)} testid="d-bol" placeholder="https://drive.google.com/..." />
          <Field label="Packing List URL" value={f.packing_list_url} onChange={(v) => upd("packing_list_url", v)} testid="d-pl" placeholder="https://drive.google.com/..." />
        </div>
        <Field label="Notes" value={f.notes} onChange={(v) => upd("notes", v)} textarea testid="d-notes" />
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="brut-btn px-3 py-2">Cancel</button>
          <button type="submit" disabled={saving} className="brut-btn brut-btn-primary px-3 py-2" data-testid="d-save">
            {saving ? "Saving..." : "Save"}
          </button>
        </div>
      </form>
    </Modal>
  );
}
